import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { wabaApi } from "../../lib/wabaApi";
import { SearchSelect, brtDayStartUtc, fmtLogTime, shiftDay, todayBrt } from "./MetricsTab";

interface ErrorRow {
  id: string;
  created_at: string;
  direction: string;
  event_type: string;
  from_number: string | null;
  to_number: string | null;
  contact_name: string | null;
  payload: unknown;
  send_status: string | null;
  send_error: string | null;
  source: string | null;
  wamid: string | null;
  connection_id: string;
  connection_name: string;
  owner_email: string | null;
  agent_id: string | null;
  agent_name: string | null;
  bsuid: string | null;
  username: string | null;
  profile_name: string | null;
}

interface ErrorsResp { errors: ErrorRow[]; page: number; has_more: boolean; owners: string[] }

const PERIODS = [{ value: 1, label: "Hoje" }, { value: 7, label: "7 dias" }, { value: 30, label: "30 dias" }];

const inputStyle: React.CSSProperties = {
  background: "var(--surface-hover)", border: "1px solid var(--line-strong)", borderRadius: 6,
  padding: "5px 8px", fontSize: 12, color: "var(--ink)",
};

// Erro legível: send_error do nosso /send, ou errors[].title da Meta no status_failed.
function errText(r: ErrorRow): string {
  if (r.send_error) return r.send_error;
  const p = r.payload as { errors?: { title?: string; message?: string; code?: number }[] } | null;
  const e = p?.errors?.[0];
  return e ? `${e.code ?? ""} ${e.title ?? e.message ?? ""}`.trim() : r.event_type;
}

function download(rows: ErrorRow[]) {
  const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `erros-waba-${todayBrt()}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function WabaErrorsTab() {
  const [period, setPeriod] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [q, setQ] = useState("");
  const [connectionId, setConnectionId] = useState("");
  const [owner, setOwner] = useState("");
  const [page, setPage] = useState(0);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const rangeActive = !!(startDate && endDate);
  const today = todayBrt();
  const dateFrom = rangeActive ? brtDayStartUtc(startDate) : brtDayStartUtc(shiftDay(today, -(period - 1)));
  const dateTo = rangeActive ? brtDayStartUtc(shiftDay(endDate, 1)) : brtDayStartUtc(shiftDay(today, 1));

  const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, page: String(page) });
  if (q.trim()) params.set("q", q.trim());
  if (connectionId) params.set("connection_id", connectionId);
  if (owner) params.set("owner_email", owner);

  const { data, isLoading, error } = useQuery<ErrorsResp>({
    queryKey: ["waba-errors", params.toString()],
    queryFn: () => wabaApi.get(`/metrics/errors?${params.toString()}`),
  });
  const { data: connList } = useQuery<{ id: string; name: string; agent_name: string | null }[]>({
    queryKey: ["waba-metrics-conns", false],
    queryFn: () => wabaApi.get("/metrics/connections"),
  });

  const rows = data?.errors ?? [];
  const allSel = rows.length > 0 && rows.every(r => sel.has(r.id));
  const toggleAll = () => setSel(allSel ? new Set() : new Set(rows.map(r => r.id)));
  const toggle = (id: string) => setSel(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const reset = () => { setPage(0); setSel(new Set()); };

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 24 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        {PERIODS.map(p => {
          const active = !rangeActive && p.value === period;
          return (
            <button key={p.value} onClick={() => { setPeriod(p.value); setStartDate(""); setEndDate(""); reset(); }}
              style={{ padding: "5px 14px", fontSize: 12, borderRadius: 99, border: `1px solid ${active ? "var(--brand)" : "var(--line-strong)"}`,
                background: active ? "rgba(195,32,32,0.12)" : "transparent", color: active ? "var(--brand)" : "var(--ink-muted)", cursor: "pointer" }}>
              {p.label}
            </button>
          );
        })}
        <div style={{ width: 1, height: 20, background: "var(--line-strong)", margin: "0 4px" }} />
        <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>De</span>
        <input type="date" value={startDate} max={endDate || today} onChange={e => { setStartDate(e.target.value); reset(); }} style={inputStyle} />
        <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>até</span>
        <input type="date" value={endDate} min={startDate || undefined} max={today} onChange={e => { setEndDate(e.target.value); reset(); }} style={inputStyle} />
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 20 }}>
        <input placeholder="Telefone, bsuid ou username" value={q} onChange={e => { setQ(e.target.value); reset(); }} style={{ ...inputStyle, minWidth: 240 }} />
        <SearchSelect value={connectionId} onChange={v => { setConnectionId(v); reset(); }} placeholder="Todas as conexões"
          options={(connList ?? []).map(c => ({ value: c.id, label: c.agent_name ? `${c.name} — ${c.agent_name}` : c.name }))} />
        <SearchSelect value={owner} onChange={v => { setOwner(v); reset(); }} placeholder="Todos os gestores"
          options={(data?.owners ?? []).map(o => ({ value: o, label: o }))} />
        <div style={{ flex: 1 }} />
        <button disabled={sel.size === 0} onClick={() => download(rows.filter(r => sel.has(r.id)))}
          style={{ fontSize: 12, padding: "6px 14px", borderRadius: 8, border: "1px solid var(--line-strong)", background: "transparent", color: sel.size ? "var(--brand)" : "var(--ink-muted)", cursor: sel.size ? "pointer" : "default" }}>
          Exportar JSON ({sel.size})
        </button>
      </div>

      {error && <div style={{ fontSize: 13, color: "var(--danger)", marginBottom: 16 }}>Erro ao carregar. Verifique se o serviço WABA está online.</div>}
      {isLoading && <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>Carregando...</div>}
      {data && rows.length === 0 && <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>Nenhum erro no período.</div>}

      {rows.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr style={{ color: "var(--ink-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", textAlign: "left" }}>
              <th style={{ padding: "6px 8px" }}><input type="checkbox" checked={allSel} onChange={toggleAll} title="Marcar todos" /></th>
              <th style={{ padding: "6px 8px" }}>Horário</th>
              <th style={{ padding: "6px 8px" }}>WABA</th>
              <th style={{ padding: "6px 8px" }}>Agente</th>
              <th style={{ padding: "6px 8px" }}>Lead</th>
              <th style={{ padding: "6px 8px" }}>Erro</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const num = r.direction === "outbound" ? r.to_number : r.from_number;
              return (
                <tr key={r.id} style={{ borderTop: "1px solid var(--line)" }}>
                  <td style={{ padding: "8px" }}><input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} /></td>
                  <td style={{ padding: "8px", whiteSpace: "nowrap", color: "var(--ink-secondary)" }}>{fmtLogTime(r.created_at)}</td>
                  <td style={{ padding: "8px", fontWeight: 600, color: "var(--ink)" }}>{r.connection_name}</td>
                  <td style={{ padding: "8px", color: "var(--ink-secondary)" }}>{r.agent_name ?? "—"}</td>
                  <td style={{ padding: "8px", color: "var(--ink-secondary)", whiteSpace: "nowrap" }}>
                    {num ?? "—"}{r.username ? ` · @${r.username}` : r.profile_name ? ` · ${r.profile_name}` : ""}
                  </td>
                  <td title={errText(r)} style={{ padding: "8px", color: "var(--danger)", maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <span style={{ fontSize: 11, color: "var(--ink-muted)", marginRight: 6 }}>{r.event_type}{r.source ? ` · ${r.source}` : ""}</span>
                    {errText(r)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {data && (page > 0 || data.has_more) && (
        <div style={{ display: "flex", gap: 8, marginTop: 16, fontSize: 12 }}>
          <button disabled={page === 0} onClick={() => { setPage(p => p - 1); setSel(new Set()); }} style={{ ...inputStyle, cursor: "pointer" }}>← Anterior</button>
          <span style={{ color: "var(--ink-muted)", alignSelf: "center" }}>Página {page + 1}</span>
          <button disabled={!data.has_more} onClick={() => { setPage(p => p + 1); setSel(new Set()); }} style={{ ...inputStyle, cursor: "pointer" }}>Próxima →</button>
        </div>
      )}
    </div>
  );
}
